import { CSSProperties, useContext, useEffect, useRef } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import { useOrientation } from 'hooks/useOrientation';
import { currencyFormatter } from 'utils/functions';
import { safeAreaPanelInsets } from 'utils/safeArea';
import { TMXPageData } from 'utils/threatmetrix';

import { CashierContext, CashierTab } from './CashierContext';
import DepositView from './DepositView';
import HistoryView from './HistoryView';
import WithdrawView from './WithdrawView';

const tabs: { key: CashierTab; label: string }[] = [
  { key: 'deposit', label: 'Deposit' },
  { key: 'withdraw', label: 'Withdraw' },
  { key: 'history', label: 'History' }
];

const CashierPanel = () => {
  const {
    isOpen,
    closeCashier,
    activeTab,
    setActiveTab,
    reset,
    selectedMethod,
    profileForPage,
    openedFromMenu
  } = useContext(CashierContext);

  const handleBack = () => {
    const shouldReopenMenu = openedFromMenu;
    closeCashier();
    if (shouldReopenMenu) {
      // Use the existing event the lobby headers already listen for to reopen
      // the account menu — no extra wiring needed in LobbyHeader/SaloonHeader.
      window.dispatchEvent(new CustomEvent('open-profile-panel'));
    }
  };

  const userBalance = useAppSelector((state) => state.user.playerBankAmount);
  const chargeBalance = useAppSelector((state) => state.user.playerChargeBalance);
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!panelRef.current || !backdropRef.current) return;

    // Always slide horizontally from the right — on both desktop and mobile.
    if (isOpen) {
      gsap.to(backdropRef.current, { opacity: 1, duration: 0.25, pointerEvents: 'auto' });
      gsap.fromTo(
        panelRef.current,
        { x: '100%', y: '0%' },
        { x: '0%', duration: 0.4, ease: 'power3.out' }
      );
    } else {
      gsap.to(panelRef.current, { x: '100%', duration: 0.3, ease: 'power2.in' });
      gsap.to(backdropRef.current, {
        opacity: 0,
        duration: 0.25,
        onComplete: () => {
          if (backdropRef.current) backdropRef.current.style.pointerEvents = 'none';
        }
      });
    }
  }, [isOpen]);

  // Trigger TMX device profiling when a transaction tab/method becomes active
  // and cache the session for the subsequent transaction. Opening the cashier
  // lands on Deposit (an initial activation); switching tabs re-profiles with
  // that tab's page index; selecting p2p within Withdraw re-profiles as
  // Transfer. History has no transaction, so it never profiles. profileForPage
  // dedupes on page index, so crypto↔cage (both Withdrawal) is a single profile.
  useEffect(() => {
    if (!isOpen || activeTab === 'history') return;
    if (activeTab === 'deposit') {
      profileForPage(TMXPageData.Deposit);
      return;
    }
    // activeTab === 'withdraw': index is a pure function of the method —
    // null/crypto/cage → Withdrawal, p2p → Transfer.
    profileForPage(selectedMethod === 'p2p' ? TMXPageData.Transfer : TMXPageData.Withdrawal);
  }, [isOpen, activeTab, selectedMethod, profileForPage]);

  const handleTabChange = (tab: CashierTab) => {
    reset();
    setActiveTab(tab);
  };

  return (
    <>
      {/* Backdrop */}
      <div ref={backdropRef} style={backdropStyle} onClick={closeCashier} />

      {/* Panel */}
      <div ref={panelRef} style={isPortrait ? panelMobileStyle : panelDesktopStyle}>
        {/* Header — title centered, back on left, close on right. */}
        <div style={headerStyle}>
          <button type='button' onClick={handleBack} style={backStyle} aria-label='Back'>
            <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
          </button>
          <div style={headerCenterStyle}>
            <span style={titleStyle}>Cashier</span>
            <div style={balanceRowStyle}>
              <span style={balanceLabelStyle}>Balance</span>
              <span style={balanceValueStyle}>{currencyFormatter(userBalance)}</span>
              {chargeBalance > 0 && (
                <>
                  <span style={balanceDividerStyle}>|</span>
                  <span style={promoValueStyle}>{currencyFormatter(chargeBalance)}</span>
                </>
              )}
            </div>
          </div>
          <button onClick={closeCashier} style={closeStyle}>
            <CloseIcon style={{ fontSize: '1.2rem' }} />
          </button>
        </div>

        {/* Tabs */}
        <div style={tabsRowStyle}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                ...tabStyle,
                ...(activeTab === tab.key ? activeTabStyle : {})
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {activeTab === 'deposit' && <DepositView />}
          {activeTab === 'withdraw' && <WithdrawView />}
          {activeTab === 'history' && <HistoryView />}
        </div>
      </div>
    </>
  );
};

export default CashierPanel;

const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  zIndex: 900,
  opacity: 0,
  pointerEvents: 'none'
};

const panelBase: CSSProperties = {
  position: 'fixed',
  backgroundColor: 'rgba(12, 18, 28, 0.98)',
  backdropFilter: 'blur(20px)',
  zIndex: 950,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  // Re-enable pointer events: when the lobby's portrait lock wraps this panel
  // (LobbyOverlayLock) the rotated layer is pointer-transparent, and pointer-
  // events is inherited — so the panel must opt back in to stay interactive.
  pointerEvents: 'auto',
  // Honor device safe areas so the header doesn't sit under the notch/status
  // bar and the scrollable content clears the home indicator in portrait.
  ...safeAreaPanelInsets
};

const panelMobileStyle: CSSProperties = {
  ...panelBase,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  height: '100vh',
  transform: 'translateY(100%)',
  boxShadow: '0 -8px 40px rgba(0, 0, 0, 0.5)'
};

const panelDesktopStyle: CSSProperties = {
  ...panelBase,
  top: 0,
  right: 0,
  bottom: 0,
  width: '380px',
  borderTopLeftRadius: '16px',
  borderBottomLeftRadius: '16px',
  transform: 'translateX(100%)',
  boxShadow: '-8px 0 40px rgba(0, 0, 0, 0.5)',
  borderLeft: '1px solid rgba(255, 255, 255, 0.06)'
};

const headerStyle: CSSProperties = {
  // Grid (28px | 1fr | 32px) guarantees the center column is geometrically
  // centered regardless of back / close button widths.
  display: 'grid',
  gridTemplateColumns: '28px 1fr 32px',
  alignItems: 'start',
  gap: 12,
  padding: '20px 20px 16px',
  // Chunky orange dashed underline matching AccountMenuPanel / Account Profile
  // header for visual consistency across panels.
  backgroundImage:
    'repeating-linear-gradient(to right, rgba(232,130,31,0.7) 0 9px, transparent 9px 15px)',
  backgroundSize: '100% 2px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'bottom left',
  flexShrink: 0
};

const backStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.75)',
  padding: 4,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  marginTop: 2,
  flexShrink: 0
};

const headerCenterStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  flex: 1,
  minWidth: 0,
  textAlign: 'center'
};

const titleStyle: CSSProperties = {
  fontSize: '1.3rem',
  fontWeight: 800,
  color: '#ffffff',
  letterSpacing: '-0.02em'
};

const balanceRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
};

const balanceLabelStyle: CSSProperties = {
  fontSize: '0.7rem',
  color: 'rgba(255, 255, 255, 0.4)',
  fontWeight: 500
};

const balanceValueStyle: CSSProperties = {
  fontSize: '0.9rem',
  fontWeight: 700,
  color: '#4CAF50'
};

const balanceDividerStyle: CSSProperties = {
  color: 'rgba(255, 255, 255, 0.15)',
  fontSize: '0.8rem'
};

const promoValueStyle: CSSProperties = {
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#E98C26'
};

const closeStyle: CSSProperties = {
  background: 'rgba(255, 255, 255, 0.06)',
  border: 'none',
  borderRadius: '10px',
  color: 'rgba(255, 255, 255, 0.5)',
  padding: '8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const tabsRowStyle: CSSProperties = {
  display: 'flex',
  gap: '2px',
  padding: '0 20px',
  marginTop: '8px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  flexShrink: 0
};

const tabStyle: CSSProperties = {
  flex: 1,
  padding: '10px 0',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  color: 'rgba(255, 255, 255, 0.5)',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.2s ease'
};

const activeTabStyle: CSSProperties = {
  color: '#E98C26',
  borderBottomColor: '#E98C26'
};

const contentStyle: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  scrollbarWidth: 'none',
  padding: '16px 20px'
};
